/**
 * AI operations. Spec 11.4: "Spend by day/user/model, quota breaches, latency and error
 * rates, model routing config."
 *
 * Spend by day and model is here. Per-USER spend is deliberately absent: it would be a
 * table of who wrote how many reflections, which is a shape of the same private activity
 * spec 11.5 keeps out of admin tooling. Day and model answer the operational question —
 * "what is this costing and which model is doing it" — without it.
 */
import type { AiSpendDto } from '@lila/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../components/ui/Alert';
import { DataTable, type Column } from '../../../components/ui/DataTable';
import { EmptyState } from '../../../components/ui/EmptyState';
import { SelectField } from '../../../components/ui/FieldParts';
import { SkeletonList } from '../../../components/ui/Skeleton';
import { useAiSpend } from '../../../features/admin/api';
import { errorKeyFor } from '../../../lib/errors';

export const Route = createFileRoute('/$lang/admin/ai')({ component: Page });

function Page(): ReactNode {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const [days, setDays] = useState(30);
  const spend = useAiSpend(days);

  const number = new Intl.NumberFormat(i18n.language);

  const columns: readonly Column<AiSpendDto>[] = [
    { id: 'day', header: t('admin:ai.spendByDay'), cell: (row) => row.day },
    { id: 'model', header: t('admin:ai.model'), cell: (row) => row.model },
    {
      id: 'requests',
      header: t('admin:ai.requests'),
      align: 'end',
      cell: (row) => number.format(row.requests),
    },
    {
      id: 'cost',
      header: t('admin:ai.cost'),
      align: 'end',
      /* Micro-USD in the database (spec 7.4); dollars are what an operator thinks in. */
      cell: (row) => `$${(row.costMicros / 1_000_000).toFixed(4)}`,
    },
    {
      id: 'in',
      header: t('admin:ai.tokensIn'),
      align: 'end',
      hideOnMobile: true,
      cell: (row) => number.format(row.tokensIn),
    },
    {
      id: 'out',
      header: t('admin:ai.tokensOut'),
      align: 'end',
      hideOnMobile: true,
      cell: (row) => number.format(row.tokensOut),
    },
    {
      id: 'cached',
      header: t('admin:ai.cached'),
      align: 'end',
      hideOnMobile: true,
      /* Spec 25.2: prompt caching is "the single largest lever", so it is worth watching. */
      cell: (row) => number.format(row.cachedTokensIn),
    },
  ];

  const total = (spend.data ?? []).reduce((sum, row) => sum + row.costMicros, 0);

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold text-ink">{t('admin:nav.ai')}</h1>

      <div className="max-w-xs">
        <SelectField
          label={t('admin:ai.spendByDay')}
          value={String(days)}
          onChange={(event) => {
            setDays(Number(event.target.value));
          }}
          options={[
            { value: '7', label: '7' },
            { value: '30', label: '30' },
            { value: '90', label: '90' },
          ]}
        />
      </div>

      {spend.error !== null && <Alert tone="error">{t(errorKeyFor(spend.error))}</Alert>}

      {spend.isLoading ? (
        <SkeletonList rows={5} label={t('admin:nav.ai')} />
      ) : (
        <>
          <p className="text-sm text-ink-muted">
            {t('admin:ai.cost')}: ${(total / 1_000_000).toFixed(2)}
          </p>
          <DataTable
            caption={t('admin:nav.ai')}
            columns={columns}
            rows={spend.data ?? []}
            rowKey={(row) => `${row.day}:${row.model}`}
            empty={<EmptyState title={t('admin:ai.empty')} />}
          />
        </>
      )}
    </div>
  );
}
